import api from './client'
import { mediaApi } from './media'

export interface DiveSummary {
  id: string
  diveNumber: number | null
  date: string
  maxDepthM: number
  durationMin: number | null
  diveType: string
  diveSiteId: string | null
  diveSiteName?: string
  privacy: string
  mediaCount: number
  tags?: { tag: string }[]
  rating?: number | null
  notes?: string
  waterTempC?: number | null
  visibilityM?: number | null
  country?: string | null
  city?: string | null
  isDemo?: boolean
  importSource?: string | null
  certifiedByCenterId?: string | null
  certifiedByCenterName?: string | null
  certifiedAt?: string | null
  divingCenterId?: string | null
  divingCenterName?: string | null
}

export interface GasCylinder {
  type: string
  gas: string
  gasMix?: string
  cylinderType?: string
  sizeL?: number
  startPressureBar?: number
  endPressureBar?: number
}

export interface DiveDetail extends DiveSummary {
  avgDepthM?: number | null
  entryTime?: string | null
  exitTime?: string | null
  airTempC?: number | null
  visibility?: string | null
  waterType?: string | null
  entryType?: string | null
  tankPressureBar?: number | null
  suitType?: string | null
  weightKg?: number | null
  isPublic?: boolean
  gasMix?: { o2Percent: number; hePercent: number; startBar?: number; endBar?: number } | null
  decoStops?: { depthM: number; timeMin: number }[]
  profiles?: { timeS: number; depthM: number; pressureBar?: number; tempC?: number; ceilingM?: number }[]
  buddies?: { userId: string }[]
  // Freediving
  diveStyle?: 'SCUBA' | 'FREEDIVING' | 'SNORKELING' | null
  breathHoldSec?: number | null
  apneaDiscipline?: string | null
  lcmEvent?: boolean
  boEvent?: boolean
  // Dive computer / sports stats
  cnsPercent?: number | null
  n2LoadPercent?: number | null
  trainingEffect?: number | null
  anaerobicTE?: number | null
  exerciseLoad?: number | null
  trainingEffectLabel?: string | null
  waterTempMinC?: number | null
  waterTempMaxC?: number | null
  waterTempAvgC?: number | null
  altitudeM?: number | null
  ascentTotalM?: number | null
  descentTotalM?: number | null
  altitudeMinM?: number | null
  altitudeMaxM?: number | null
  exitLatitude?: number | null
  exitLongitude?: number | null
  gasCylinders?: GasCylinder[] | null
  // Professional (L.9/2026)
  isProfessional?: boolean
  employerUserId?: string | null
  employerSignature?: string | null
  certifiedByCenterId?: string | null
  certifiedByCenterName?: string | null
  certifiedAt?: string | null
}

export interface DiveListResult {
  dives: DiveSummary[]
  total: number
  nextCursor: string | null
}

export interface CreateDivePayload {
  date: string
  diveSiteId?: string
  diveSiteName?: string
  country?: string
  city?: string
  entryTime?: string
  exitTime?: string
  maxDepthM: number
  avgDepthM?: number
  durationMin: number
  waterTempC?: number
  airTempC?: number
  visibilityM?: number
  gasMix?: { o2Percent: number; hePercent?: number; startBar?: number; endBar?: number }
  decoStops?: { depthM: number; timeMin: number }[]
  diveType?: string
  notes?: string
  privacy?: string
  buddyIds?: string[]
  buddyNames?: string[]
  tags?: string[]
  suitType?: string
  weightKg?: number
  tankPressureBar?: number
  entryType?: string
  waterType?: string
  rating?: number
  // Diving center che ha organizzato l'immersione (dal piano o scelto a mano)
  divingCenterId?: string | null
  divingCenterName?: string | null
  // Freediving fields
  diveStyle?: 'SCUBA' | 'FREEDIVING' | 'SNORKELING'
  breathHoldSec?: number
  apneaDiscipline?: string
  lcmEvent?: boolean
  boEvent?: boolean
}

export interface UpdateDivePayload {
  diveDate?: string
  diveSiteId?: string
  diveSiteName?: string
  country?: string
  city?: string
  entryTime?: string
  exitTime?: string
  maxDepthM?: number
  avgDepthM?: number
  durationMin?: number
  waterTempC?: number
  airTempC?: number
  visibility?: 'POOR' | 'LIMITED' | 'GOOD' | 'EXCELLENT'
  waterType?: 'FRESH' | 'SALT' | 'BRACKISH'
  entryType?: 'SHORE' | 'BOAT' | 'LIVEABOARD' | 'ICE'
  diveType?: string   // il backend (UpdateDiveSchema) accetta l'enum; prima non veniva inviato in modifica
  tankPressureBar?: number
  suitType?: string
  weightKg?: number
  notes?: string
  rating?: number
  divingCenterId?: string | null
  divingCenterName?: string | null
  isPublic?: boolean
  privacy?: 'PRIVATE' | 'FRIENDS' | 'PUBLIC'
  tags?: string[]
  buddyIds?: string[]
  buddyNames?: string[]
  // Freediving fields
  diveStyle?: 'SCUBA' | 'FREEDIVING' | 'SNORKELING'
  breathHoldSec?: number
  apneaDiscipline?: string
  lcmEvent?: boolean
  boEvent?: boolean
  gasMix?: { o2Percent: number; hePercent?: number; startBar?: number; endBar?: number }
}

export const LOGBOOK_PAGE_LIMIT = parseInt(import.meta.env.VITE_LOGBOOK_PAGE_LIMIT ?? '500')

export type DiveGoalType = 'DIVE_COUNT' | 'MAX_DEPTH' | 'TOTAL_HOURS' | 'UNIQUE_SITES' | 'UNIQUE_COUNTRIES'

export interface DiveGoal {
  id: string
  userId: string
  type: DiveGoalType
  targetValue: number
  year: number
  isActive: boolean
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface DiveGoalWithProgress extends DiveGoal {
  currentValue: number
}

// ── Public user logbook stats ─────────────────────────────────────────────────

export interface PublicDiverStats {
  totalDives: number
  maxDepthM: number | null
  totalMinutes: number
  uniqueCountries: number
  recentDives: {
    id: string
    date: string
    maxDepthM: number | null
    durationMin: number | null
    siteName: string | null
    country: string | null
    rating: number | null
  }[]
}

export const publicLogbookApi = {
  getUserStats: (userId: string) =>
    api.get<PublicDiverStats>(`/v1/public/users/${userId}/stats`).then(r => r.data),

  getPublicDive: (diveId: string) =>
    api.get<DiveDetail>(`/v1/public/dives/${diveId}`).then(r => r.data),

  getFeaturedDives: (userId: string) =>
    api.get<FeaturedDiveItem[]>(`/v1/public/users/${userId}/featured`).then(r => r.data),

  getDiveByShareToken: (token: string) =>
    api.get<DiveDetail>(`/v1/public/dives/share/${token}`).then(r => r.data),
}

export const diveShareApi = {
  create: (diveId: string) =>
    api.post<{ shareToken: string }>(`/v1/dives/${diveId}/share`).then(r => r.data),

  revoke: (diveId: string) =>
    api.delete(`/v1/dives/${diveId}/share`),
}

// ── Logbook Metrics (hypertable) ──────────────────────────────────────────────

export interface MetricPoint {
  period: string   // ISO date string
  diveCount: number
  totalMin: number
  maxDepthM: number | null
  avgDepthM: number | null
  avgTempC: number | null
}

export interface MetricsResponse {
  agg: 'day' | 'week' | 'month'
  from: string
  to: string
  source: 'hypertable' | 'live'
  data: MetricPoint[]
}

export const logbookMetricsApi = {
  get: (params?: { from?: string; to?: string; agg?: 'day' | 'week' | 'month'; limit?: number }) =>
    api.get<MetricsResponse>('/v1/logbook/metrics', { params }).then(r => r.data),

  refresh: () =>
    api.post('/v1/logbook/metrics/refresh').then(r => r.data),
}

// ── Dive Validation ───────────────────────────────────────────────────────────

export type ValidationStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

export interface DiveValidation {
  id: string
  diveId: string
  diverId: string
  instructorId: string
  status: ValidationStatus
  diverNotes: string | null
  instructorNotes: string | null
  requestedAt: string
  respondedAt: string | null
}

export interface ValidationQueueItem extends DiveValidation {
  dive: {
    id: string
    diveDate: string
    maxDepthM: number | null
    durationMin: number | null
    diveSiteName: string | null
    country: string | null
    diveType: string
    userId: string
  } | null
}

export const validationApi = {
  request: (diveId: string, data: { instructorId: string; diverNotes?: string }) =>
    api.post<DiveValidation>(`/v1/dives/${diveId}/validation-request`, data).then(r => r.data),

  listForDive: (diveId: string) =>
    api.get<DiveValidation[]>(`/v1/dives/${diveId}/validations`).then(r => r.data),

  queue: () =>
    api.get<ValidationQueueItem[]>('/v1/dives/validation-queue').then(r => r.data),

  respond: (id: string, data: { status: 'APPROVED' | 'REJECTED'; instructorNotes?: string }) =>
    api.patch<DiveValidation>(`/v1/dives/validations/${id}`, data).then(r => r.data),

  cancel: (diveId: string, instructorId: string) =>
    api.delete(`/v1/dives/${diveId}/validation-request/${instructorId}`),
}

// ── Featured Dives ────────────────────────────────────────────────────────────

export interface FeaturedDiveItem {
  diveId: string
  sortOrder: number
  date?: string
  maxDepthM: number | null
  durationMin: number | null
  siteName: string | null
  country: string | null
  rating: number | null
  diveType?: string
}

export interface FeaturedDiveEntry {
  id: string
  diveId: string
  sortOrder: number
  dive: {
    id: string
    date: string
    maxDepthM: number | null
    durationMin: number | null
    diveSiteName: string | null
    country: string | null
    rating: number | null
    diveType: string
    isPublic: boolean
  }
}

export const featuredDivesApi = {
  list: () =>
    api.get<FeaturedDiveEntry[]>('/v1/logbook/featured').then(r => r.data),

  update: (diveIds: string[]) =>
    api.put('/v1/logbook/featured', { diveIds }),

  remove: (diveId: string) =>
    api.delete(`/v1/logbook/featured/${diveId}`),
}

export interface SubmissionItem {
  id: string
  filename: string
  fileSize: number
  status: 'PENDING' | 'PROCESSING' | 'DONE' | 'ERROR' | 'REJECTED'
  userMessage: string | null
  adminNotes: string | null
  importResult: { imported: number; skipped: number; errors: string[] } | null
  submittedAt: string
  processedAt: string | null
}

export interface AdminSubmissionItem extends SubmissionItem {
  userId: string
  userEmail: string | null
}

export interface DateChangeRequest {
  id: string
  diveId: string
  userId: string
  userEmail?: string | null
  diveSiteName?: string | null
  currentDate: string
  requestedDate: string
  motivation?: string | null
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  requestedAt: string
  reviewedBy?: string | null
  reviewedAt?: string | null
  rejectionReason?: string | null
}

export const dateChangeAdminApi = {
  list: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get<{ data: DateChangeRequest[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(
      '/v1/dives/admin/date-change-requests', { params }
    ).then((r) => r.data),
  approve: (id: string) =>
    api.patch<{ ok: boolean }>(`/v1/dives/admin/date-change-requests/${id}/approve`).then((r) => r.data),
  reject: (id: string, rejectionReason?: string) =>
    api.patch<{ ok: boolean }>(`/v1/dives/admin/date-change-requests/${id}/reject`, { rejectionReason }).then((r) => r.data),
}

export const logbookApi = {
  list: (params?: { limit?: number; cursor?: string; from?: string; to?: string; diveSiteId?: string }) =>
    api.get<DiveListResult>('/v1/dives', { params }).then((r) => r.data),

  get: (id: string) => api.get<DiveDetail>(`/v1/dives/${id}`).then((r) => r.data),

  create: (data: CreateDivePayload) =>
    api.post<{ id: string; restrictedZones?: unknown[] }>('/v1/dives', data).then((r) => r.data),

  update: (id: string, data: UpdateDivePayload) =>
    api.patch(`/v1/dives/${id}`, data).then((r) => r.data),

  // Richiesta cambio data immersione (validata da admin)
  getDateChangeRequest: (id: string) =>
    api.get<DateChangeRequest | null>(`/v1/dives/${id}/date-change-request`).then((r) => r.data),
  requestDateChange: (id: string, data: { requestedDate: string; motivation?: string }) =>
    api.post<DateChangeRequest>(`/v1/dives/${id}/date-change-request`, data).then((r) => r.data),

  delete: (id: string, config?: any) => api.delete(`/v1/dives/${id}`, config).then((r) => r.data),

  import: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ imported: number; skipped: number; errors: string[] }>(
      '/v1/dives/import',
      form,
    ).then((r) => r.data)
  },

  // L-10: preview delle colonne di un CSV quando l'auto-detect ha fallito.
  // Il backend ritorna header + prime 5 righe + eventuale auto-detect come
  // default pre-selezionato nella UI di mapping manuale.
  csvPreview: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{
      headers: string[]
      sample: string[][]
      autoMap: Record<string, number>
      rowCount: number
      fields: string[]
    }>('/v1/dives/import/csv-preview', form).then((r) => r.data)
  },

  // L-10: reimporta il CSV con il mapping scelto dall'utente. Il backend
  // riusa il dedup L-3 (externalId/contentHash) via importParsedDives.
  csvImportMapped: (file: File, mapping: Record<string, number>) => {
    const form = new FormData()
    form.append('file', file)
    form.append('mapping', JSON.stringify(mapping))
    return api.post<{ imported: number; skipped: number; errors: string[] }>(
      '/v1/dives/import/csv-mapped',
      form,
    ).then((r) => r.data)
  },

  getImportLog: () =>
    api.get<Array<{
      id: string
      source: string
      fileName: string | null
      totalParsed: number
      imported: number
      duplicates: number
      errors: number
      createdAt: string
    }>>('/v1/dives/import/log').then((r) => r.data),

  adminImport: (userId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ imported: number; skipped: number; errors: string[] }>(
      `/v1/dives/admin/import?userId=${encodeURIComponent(userId)}`,
      form,
    ).then((r) => r.data)
  },

  // ── Submission queue ────────────────────────────────────────────────────────
  submitForReview: (file: File, message?: string) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ id: string; status: string }>(
      '/v1/dives/submissions',
      form,
      { params: message ? { message } : {} }
    ).then((r) => r.data)
  },

  mySubmissions: () =>
    api.get<SubmissionItem[]>('/v1/dives/submissions').then((r) => r.data),

  adminSubmissions: (status?: string) =>
    api.get<AdminSubmissionItem[]>('/v1/dives/submissions/admin', { params: status ? { status } : undefined }).then((r) => r.data),

  adminProcessSubmission: (id: string) =>
    api.post<{ imported: number; skipped: number; errors: string[] }>(`/v1/dives/submissions/admin/${id}/process`).then((r) => r.data),

  adminRejectSubmission: (id: string, notes?: string) =>
    api.post(`/v1/dives/submissions/admin/${id}/reject`, { notes }).then((r) => r.data),

  stats: () => api.get<{
    totalDives: number
    totalTimeMin: number
    maxDepthM: number | null
    avgDepthM: number | null
    countriesVisited: number
    uniqueSites: number
  }>('/v1/dives/stats').then((r) => r.data),

  timeline: (granularity: 'month' | 'year' = 'month') =>
    api.get<{ period: string; dives: number; totalTimeMin: number; maxDepthM: number | null }[]>(
      '/v1/dives/stats/timeline', { params: { granularity } }
    ).then((r) => r.data),

  depthHistogram: () =>
    api.get<{ bucket: string; count: number }[]>('/v1/dives/stats/depth-histogram').then((r) => r.data),

  topSites: (limit = 5) =>
    api.get<{ siteName: string; siteId: string | null; diveCount: number; maxDepthM: number | null; avgDepthM: number | null }[]>(
      '/v1/dives/stats/top-sites', { params: { limit } }
    ).then((r) => r.data),

  // Siti già visitati dall'utente (per i suggerimenti rapidi nel selettore sito).
  visitedSites: () =>
    api.get<{ sites: { diveSiteId: string | null; diveSiteName: string | null; visits: number; lastVisit: string }[]; total: number }>(
      '/v1/dives/sites/visited'
    ).then((r) => r.data),

  sacTrend: () =>
    api.get<{ cohort: number; avgSac: number | null; diveCount: number }[]>(
      '/v1/dives/stats/sac-trend'
    ).then((r) => r.data),

  qualifyingCounts: () =>
    api.get<{
      totalDives: number
      nightDives: number
      deepDives_18plus: number
      deepDives_30plus: number
      deepDives_40plus: number
      decoDives: number
      caveDives: number
      wreckDives: number
      iceDives: number
      altitudeDives: number
      drysuitDives: number
      freeDivingDives: number
      driftDives: number
    }>('/v1/dives/stats/qualifying-counts').then((r) => r.data),

  heatmap: (year?: number) =>
    api.get<{ date: string; count: number }[]>(
      '/v1/dives/stats/heatmap', { params: year ? { year } : {} }
    ).then((r) => r.data),

  byCountry: () =>
    api.get<{ country: string; count: number }[]>(
      '/v1/dives/stats/by-country'
    ).then((r) => r.data),

  depthProgression: () =>
    api.get<{ date: string; maxDepth: number | null; avgDepth: number | null }[]>(
      '/v1/dives/stats/depth-progression'
    ).then((r) => r.data),

  temperatureSeasonal: () =>
    api.get<{ month: number; avgTemp: number | null; count: number }[]>(
      '/v1/dives/stats/temperature-seasonal'
    ).then((r) => r.data),

  worldMap: () =>
    api.get<{ siteName: string | null; siteId: string | null; diveCount: number; country: string | null }[]>(
      '/v1/dives/stats/world-map'
    ).then((r) => r.data),

  records: () =>
    api.get<{
      longestDiveMin: number | null
      deepestDiveM:   number | null
      nightDives:     number
      maxDivesInDay:  number
      currentStreak:  number
      bestStreak:     number
      byStyle: { style: string; count: number }[]
    }>('/v1/dives/stats/records').then((r) => r.data),

  syncDivelogs: (creds: { username: string; password: string }) =>
    api.post<{ imported: number; skipped: number; errors: string[] }>(
      '/v1/dives/sync/divelogs',
      creds,
    ).then((r) => r.data),

  syncShearwater: (creds: { email: string; password: string }) =>
    api.post<{ imported: number; skipped: number; errors: string[] }>(
      '/v1/dives/sync/shearwater',
      creds,
    ).then((r) => r.data),

  oauthStatus: (provider: 'garmin' | 'suunto' | 'divelogs' | 'shearwater') =>
    api.get<{ connected: boolean; lastSyncAt?: string }>(
      `/v1/dives/oauth/${provider}/status`
    ).then((r) => r.data),

  oauthAuthUrl: (provider: 'garmin' | 'suunto', codeChallenge?: string) =>
    api.get<{ url: string; state: string }>(
      `/v1/dives/oauth/${provider}/auth-url`,
      { params: codeChallenge ? { codeChallenge } : {} }
    ).then((r) => r.data),

  oauthConnect: (provider: 'garmin' | 'suunto', payload: { code: string; state: string; codeVerifier?: string }) =>
    api.post<{ imported: number; skipped: number; errors: string[] }>(
      `/v1/dives/oauth/${provider}/connect`,
      payload,
    ).then((r) => r.data),

  oauthSync: (provider: 'garmin' | 'suunto' | 'divelogs' | 'shearwater') =>
    api.post<{ imported: number; skipped: number; errors: string[] }>(
      `/v1/dives/oauth/${provider}/sync`
    ).then((r) => r.data),

  oauthDisconnect: (provider: 'garmin' | 'suunto' | 'divelogs' | 'shearwater') =>
    api.delete(`/v1/dives/oauth/${provider}`).then(() => undefined),

  connectWithCredentials: (provider: 'garmin' | 'divelogs' | 'shearwater', username: string, password: string) =>
    api.post<{ imported: number; skipped: number; errors?: string[] }>(
      `/v1/dives/oauth/${provider}/connect-credentials`, { username, password }
    ).then((r) => r.data),

  // ── Dive Goals ───────────────────────────────────────────────────────────────

  listGoals: () =>
    api.get<DiveGoalWithProgress[]>('/v1/logbook/goals').then(r => r.data),

  upsertGoal: (data: { type: DiveGoalType; targetValue: number; year?: number }) =>
    api.put<DiveGoal>('/v1/logbook/goals', data).then(r => r.data),

  deleteGoal: (type: DiveGoalType) =>
    api.delete(`/v1/logbook/goals/${type}`),

  goalsProgress: () =>
    api.get<Record<DiveGoalType, number>>('/v1/logbook/goals/progress').then(r => r.data),

  // ── Dive Computer Directory ──────────────────────────────────────────────────

  listComputers: (params?: { q?: string; manufacturer?: string; page?: number; limit?: number }) =>
    api.get<{
      items: { id: string; manufacturer: string; modelName: string; exportFormats: string[]; ediveCompatible?: boolean; primaryFormat?: string }[]
      total: number; page: number; pages: number; limit: number
    }>('/v1/dives/computers', { params }).then(r => r.data),

  getComputer: (id: string) =>
    api.get<{
      id: string; manufacturer: string; modelName: string; exportFormats: string[]
      exportMethods?: string[]; requiredSoftware?: string; softwareUrl?: string
      ediveCompatible?: boolean; primaryFormat?: string
      importSteps: { step: number; title: string; description: string; tip?: string }[]
    }>(`/v1/dives/computers/${id}`).then(r => r.data),

  suggestComputer: (data: { manufacturer?: string; modelName?: string }) =>
    api.post<{ count: number; suggestions: { id: string; manufacturer: string; modelName: string; matchScore: number }[] }>(
      '/v1/dives/computers/suggest', data
    ).then(r => r.data),

  // ── Safety Check-In ─────────────────────────────────────────────────────────

  checkIn: (data: {
    siteName: string
    latitude?: number
    longitude?: number
    plannedDepthM?: number
    plannedDurationMin?: number
    buddyUserId?: string
    buddyName?: string
    emergencyContact?: string
  }) =>
    api.post<{
      id: string; userId: string; siteName: string
      latitude: number | null; longitude: number | null
      plannedDepthM: number | null; plannedDurationMin: number
      buddyUserId: string | null; buddyName: string | null
      emergencyContact: string | null
      checkedInAt: string; autoAlertAt: string; isActive: boolean
    }>('/v1/dives/check-in', data).then(r => r.data),

  activeCheckIn: () =>
    api.get<{
      id: string; siteName: string; latitude: number | null; longitude: number | null
      plannedDepthM: number | null; plannedDurationMin: number
      buddyName: string | null; emergencyContact: string | null
      checkedInAt: string; autoAlertAt: string; isActive: boolean
    } | null>('/v1/dives/check-in/active')
      .then(r => r.data).catch(() => null),

  checkOut: (id: string) =>
    api.patch(`/v1/dives/check-in/${id}/check-out`).then(r => r.data),

  cancelCheckIn: (id: string) =>
    api.delete(`/v1/dives/check-in/${id}`).then(r => r.data),

  triggerSos: (data: { latitude?: number; longitude?: number; accuracy_m?: number; altitude_m?: number; message?: string }) =>
    api.post<{ id: string; isActive: boolean; triggeredAt: string }>(
      '/v1/dives/sos',
      data
    ).then(r => r.data),

  resolveSos: (id: string) =>
    api.patch(`/v1/dives/sos/${id}/resolve`).then(r => r.data),

  activeSos: () =>
    api.get<{ id: string; isActive: boolean; triggeredAt: string; latitude: number | null; longitude: number | null }>(
      '/v1/dives/sos/active', { validateStatus: (s) => s === 200 || s === 404 }
    ).then(r => r.status === 404 ? null : r.data),

  reconcile: (dives: Array<{ date: string; maxDepthM: number; durationMin: number; diveSiteName?: string | null; entryTime?: string | null }>) =>
    api.post<{
      total: number
      matched: number
      results: Array<{
        index: number
        date: string
        maxDepthM: number
        durationMin: number
        diveSiteName: string | null
        match: {
          id: string
          diveNumber: number | null
          date: string
          maxDepthM: number
          durationMin: number | null
          diveSiteName: string | null
          certifiedByCenterName: string | null
          importSource: string | null
          confidence: 'HIGH' | 'MEDIUM' | 'LOW'
          wasDeleted?: boolean
        } | null
      }>
    }>('/v1/dives/reconcile', { dives }).then(r => r.data),

  // Import con decisioni dell'utente (dopo reconcile): per ogni dive IMPORT/SKIP/MERGE
  importResolved: (dives: Array<{
    action: 'IMPORT' | 'SKIP' | 'MERGE'
    mergeTargetId?: string | null
    overwrite?: boolean
    dive: {
      date: string
      diveNumber?: number | null
      diveSiteName?: string | null
      country?: string | null
      city?: string | null
      maxDepthM?: number | null
      avgDepthM?: number | null
      durationMin?: number | null
      waterTempC?: number | null
      airTempC?: number | null
      entryTime?: string | null
      gasO2?: number | null
      gasHe?: number | null
      tankPressureBar?: number | null
      notes?: string | null
      importSource?: string | null
    }
  }>) =>
    api.post<{ imported: number; merged: number; skipped: number; errors: string[] }>(
      '/v1/dives/import-resolved', { dives }).then(r => r.data),

  // F2 — dive che combaciano con un piano (scoring), per la riconciliazione assistita
  matchPlan: (params: { plannedDate?: string; diveSiteId?: string; diveSiteName?: string; targetDepthM?: number; plannedMinutes?: number; windowDays?: number; limit?: number }) =>
    api.post<Array<{
      id: string
      diveNumber: number | null
      date: string
      maxDepthM: number
      durationMin: number | null
      diveSiteName: string | null
      matchScore: number
    }>>('/v1/dives/match-plan', params).then(r => r.data),

  exportUddf: (range?: ExportRange): Promise<void> =>
    downloadExport('uddf', 'application/xml', 'uddf', range),

  exportCsv: (range?: ExportRange): Promise<void> =>
    downloadExport('csv', 'text/csv', 'csv', range),

  // L-8: DL7 (DAN) e' il formato con cui si donano i log alla ricerca DAN.
  // Il backend genera i record ZXU/ZRH/ZDH/ZDP/ZDT con estensione .zxu.
  exportDl7: (range?: ExportRange): Promise<void> =>
    downloadExport('dl7', 'text/plain', 'zxu', range),

  // L-8: PDF server-side (pdfkit). Layout testuale essenziale, multi-pagina
  // per bulk. Il PDF "ricco" con grafici SVG resta client-side (DiveDetailPDF
  // + DiveLogPDF) perche' richiede render SVG → canvas → pdf.
  exportPdfBatch: (range?: ExportRange): Promise<void> =>
    downloadExport('pdf', 'application/pdf', 'pdf', range),
}

/**
 * L-8: intervallo facoltativo per gli export del logbook.
 *  - `from`/`to`: range date (secca → inizio/fine giornata lato client)
 *  - `ids`: filtro per UUID (singola immersione o selezione) — il backend
 *    accetta sia `ids=UUID1,UUID2` sia `ids=UUID`, cosi' lo stesso endpoint
 *    serve sia l'export completo sia l'export per-singola immersione.
 */
export type ExportRange = { from?: string; to?: string; ids?: string[] }

async function downloadExport(
  format: 'uddf' | 'csv' | 'dl7' | 'pdf',
  mime: string,
  ext: string,
  range?: ExportRange,
): Promise<void> {
  const params: Record<string, string> = {}
  // il backend vuole date-time ISO complete: una data secca verrebbe rifiutata da Zod
  if (range?.from) params.from = new Date(range.from).toISOString()
  if (range?.to) params.to = new Date(`${range.to}T23:59:59`).toISOString()
  if (range?.ids && range.ids.length > 0) params.ids = range.ids.join(',')

  const response = await api.get(`/v1/dives/export/${format}`, { params, responseType: 'blob' })
  const url = URL.createObjectURL(new Blob([response.data], { type: mime }))
  const a = document.createElement('a')
  a.href = url
  // Per singola immersione il filename ha l'id corto; per bulk la data.
  const stamp = range?.ids?.length === 1 ? range.ids[0].slice(0, 8) : new Date().toISOString().slice(0, 10)
  a.download = `edive-logbook-${stamp}.${ext}`
  a.click()
  URL.revokeObjectURL(url)
}

export async function uploadDivePhoto(
  diveId: string,
  file: File,
  currentTags: string[]
): Promise<string> {
  const asset = await mediaApi.upload(file)
  const photoTag = `photo:${asset.id}`
  const existingPhotoTags = currentTags.filter(t => t.startsWith('photo:'))
  const otherTags = currentTags.filter(t => !t.startsWith('photo:'))
  const newTags = [...existingPhotoTags, photoTag, ...otherTags].slice(0, 20)
  await api.patch(`/v1/dives/${diveId}`, { tags: newTags })
  return asset.id
}

export function getPhotoTagsFromDive(dive: DiveDetail | DiveSummary): string[] {
  return (dive.tags ?? [])
    .map((t) => typeof t === 'string' ? t : (t as any).tag)
    .filter((t) => t?.startsWith('photo:'))
    .map((t) => t.replace('photo:', ''))
}

export function getNonPhotoTags(dive: DiveDetail | DiveSummary): string[] {
  return (dive.tags ?? [])
    .map((t) => typeof t === 'string' ? t : (t as any).tag)
    .filter((t) => t && !t.startsWith('photo:'))
}

// ── Peer Dive Comparison ────────────────────────────────────────────────────

export type ComparisonStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED'

export interface DiveComparison {
  id: string
  requesterId: string
  requesterDiveId: string
  responderId: string | null
  responderDiveId: string | null
  inviteToken: string
  inviteLink?: string
  status: ComparisonStatus
  message: string | null
  requestedAt: string
  respondedAt: string | null
  expiresAt: string
}

export interface ComparisonResult {
  comparison: DiveComparison
  result: {
    requester: { diveId: string; isMe: boolean; data: Record<string, unknown> }
    responder: { diveId: string; isMe: boolean; data: Record<string, unknown> }
    diff: Record<string, { a: unknown; b: unknown; diffNum?: number }>
    fieldsMatched: number
    fieldsCompared: number
  } | null
  error?: string
}

export const comparisonApi = {
  create: (diveId: string, responderId?: string, message?: string) =>
    api.post<DiveComparison & { inviteLink: string }>('/v1/dives/comparisons', {
      diveId, responderId, message,
    }).then(r => r.data),

  list: (role?: 'all' | 'sent' | 'received') =>
    api.get<DiveComparison[]>('/v1/dives/comparisons', { params: { role } }).then(r => r.data),

  get: (id: string) =>
    api.get<ComparisonResult>(`/v1/dives/comparisons/${id}`).then(r => r.data),

  getByToken: (token: string) =>
    api.get<DiveComparison>(`/v1/dives/comparisons/token/${token}`).then(r => r.data),

  accept: (id: string, diveId: string) =>
    api.patch<DiveComparison>(`/v1/dives/comparisons/${id}/accept`, { diveId }).then(r => r.data),

  decline: (id: string) =>
    api.patch<DiveComparison>(`/v1/dives/comparisons/${id}/decline`, {}).then(r => r.data),

  cancel: (id: string) =>
    api.delete(`/v1/dives/comparisons/${id}`),
}

// ── MPA / Zone Marine Protette (L.70/2026 art.13-14) ────────────────────────

export interface MpaZone {
  id: string
  siteName: string
  authority: string
  restrictionType: 'MPA' | 'CULTURAL_HERITAGE' | 'FISHING_PROHIBITED' | 'ANCHOR_PROHIBITED' | 'ACCESS_RESTRICTED'
  lat: number
  lng: number
  radiusM: number
  description?: string | null
  externalRef?: string | null
  validFrom?: string | null
  validUntil?: string | null
}

export interface MpaCheckResult {
  lat: number
  lng: number
  hasRestrictions: boolean
  restrictions: Array<MpaZone & { distanceM: number }>
}

export const mpaApi = {
  checkCoordinates: (lat: number, lng: number) =>
    api.post<MpaCheckResult>('/v1/logbook/mpa/check', { lat, lng }).then(r => r.data),

  listZones: () =>
    api.get<MpaZone[]>('/v1/logbook/mpa').then(r => r.data),
}

// ── Professional Logbook (L.9/2026) ──────────────────────────────────────────

export interface ProfessionalDive {
  id: string
  diveDate: string
  diveNumber: number | null
  diveSiteName: string | null
  country: string | null
  maxDepthM: number | null
  durationMin: number | null
  diveType: string | null
  gasMix: string | null
  isProfessional: boolean
  employerUserId: string | null
  employerSignature: string | null
  certifiedByCenterId: string | null
  certifiedByCenterName: string | null
  certifiedAt: string | null
  notes: string | null
}

export const professionalLogbookApi = {
  list: () =>
    api.get<ProfessionalDive[]>('/v1/logbook/professional-logbook').then(r => r.data),

  markProfessional: (diveId: string, payload: {
    isProfessional: boolean
    employerSignature?: string
    employerUserId?: string
  }) =>
    api.patch<ProfessionalDive>(`/v1/logbook/${diveId}/professional`, payload).then(r => r.data),

  downloadLibretto: async () => {
    const resp = await api.get('/v1/logbook/professional-logbook/export', { responseType: 'blob' })
    const blob = new Blob([resp.data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const cd = (resp.headers['content-disposition'] as string | undefined) ?? ''
    const match = cd.match(/filename="([^"]+)"/)
    a.download = match?.[1] ?? `libretto-professionale-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },
}
